import { useQuery } from '@tanstack/react-query';
import { ENV } from '../config/env';

interface MonitorStatus {
  name: string;
  status: string;
}

interface SystemStatus {
  status: 'operational' | 'degraded' | 'downtime' | 'maintenance' | 'unknown';
  monitors: MonitorStatus[];
  updatedAt: string;
  source?: string;
}

const STATUS_LABELS: Record<string, string> = {
  operational: 'Operational',
  degraded: 'Degraded',
  downtime: 'Major Outage',
  maintenance: 'Maintenance',
  unknown: 'Unknown',
};

const STATUS_COLORS: Record<string, { dot: string; text: string }> = {
  operational: { dot: 'fill-brand text-brand', text: 'text-muted-foreground' },
  degraded: { dot: 'fill-review text-review', text: 'text-review' },
  downtime: { dot: 'fill-enforcement text-enforcement', text: 'text-enforcement' },
  maintenance: { dot: 'fill-[var(--info)] text-[var(--info)]', text: 'text-muted-foreground' },
  unknown: { dot: 'fill-muted-foreground text-muted-foreground', text: 'text-muted-foreground' },
};

async function fetchSystemStatus(): Promise<SystemStatus> {
  const res = await fetch(`${ENV.API_BASE_URL}/health/status`, {
    signal: AbortSignal.timeout(5000),
  });
  if (!res.ok) throw new Error(`Status API returned ${res.status}`);
  return res.json();
}

export function useSystemStatus() {
  const query = useQuery<SystemStatus>({
    queryKey: ['system-status'],
    queryFn: fetchSystemStatus,
    refetchInterval: 60_000,
    staleTime: 30_000,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  const status = query.data?.status ?? 'operational';
  const label = STATUS_LABELS[status] ?? 'Operational';
  const colors = STATUS_COLORS[status] ?? STATUS_COLORS.operational;

  return {
    status,
    label,
    colors,
    monitors: query.data?.monitors ?? [],
    updatedAt: query.data?.updatedAt ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    source: query.data?.source ?? null,
  };
}
